import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  LoaderCircle,
  Plus,
  Upload,
  Workflow,
} from "lucide-react";
import {
  EmptyState,
  FeedbackBanner,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";

export function WorkflowTemplatesPage() {
  const api = useApi();
  const { navigate } = useRouter();
  const importInputRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    api.listWorkflowTemplates()
      .then((result) => {
        if (!active) return;
        setTemplates(result.items);
        setCanManage(Boolean(result.permissions?.canManage));
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [api]);

  const importWorkflow = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file || importing) return;
    setImporting(true);
    setError("");
    setNotice("");
    try {
      const imported = await api.importWorkflowTemplate(file);
      setTemplates((current) => [
        imported,
        ...current.filter(({ id }) => id !== imported.id),
      ]);
      setNotice(`Importamos “${imported.name}” correctamente.`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      input.value = "";
      setImporting(false);
    }
  };

  return (
    <div className="page workflow-templates-page">
      <PageHeader
        hero
        eyebrow="Praxis · Configuración"
        title="Flujos"
        description="Diseñá flujos personalizados con la información que necesita cada proceso de trabajo."
        actions={canManage ? (
          <div className="workflow-template-page-actions">
            <input
              ref={importInputRef}
              hidden
              type="file"
              accept=".json,application/json"
              onChange={importWorkflow}
            />
            <button
              className="button secondary"
              type="button"
              disabled={importing}
              onClick={() => importInputRef.current?.click()}
            >
              {importing
                ? <LoaderCircle className="spin" size={17} />
                : <Upload size={17} />}
              {importing ? "Importando…" : "Importar JSON"}
            </button>
            <button
              className="button primary"
              type="button"
              onClick={() => navigate("/flujos/nuevo")}
            >
              <Plus size={17} />
              Nuevo flujo
            </button>
          </div>
        ) : null}
      />

      {error && <FeedbackBanner tone="error">{error}</FeedbackBanner>}
      {notice && (
        <FeedbackBanner tone="success">{notice}</FeedbackBanner>
      )}

      {loading ? (
        <div className="workflow-templates-loading">
          <LoaderCircle className="spin" size={23} />
          <span>Cargando flujos…</span>
        </div>
      ) : templates.length ? (
        <section className="workflow-template-grid" aria-label="Flujos">
          {templates.map((template) => (
            <article
              className="workflow-template-card"
              key={template.id}
              role="link"
              tabIndex={0}
              aria-label={`Abrir registros de ${template.name}`}
              onClick={() => navigate(`/flujos/${template.id}`)}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(`/flujos/${template.id}`);
                }
              }}
            >
              <div className="workflow-template-card-icon">
                <Workflow size={20} />
              </div>
              <div className="workflow-template-card-copy">
                <h2>{template.name}</h2>
                <p>{template.description || "Sin descripción."}</p>
              </div>
              <div className="workflow-template-card-actions">
                <span className="workflow-template-open" aria-hidden="true">
                  Abrir registros
                  <ArrowRight size={16} />
                </span>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <EmptyState
          icon={Workflow}
          title="Todavía no hay flujos"
          description="Creá un flujo personalizado y definí la información que necesita tu proceso de trabajo."
          action={canManage ? (
            <button
              className="button primary"
              type="button"
              onClick={() => navigate("/flujos/nuevo")}
            >
              <Plus size={17} />
              Crear flujo
            </button>
          ) : null}
        />
      )}
    </div>
  );
}
